/**
 * Thin REST client. Each service URL is built via env at build-time
 * (VITE_API_*) — defaults assume a single ingress at `/v1/<service-name>/...`.
 * Memoria feedback_dhk8_ingress_pattern: TODA URL externa requiere prefix.
 */

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

export interface ApiOk<T> { ok: true; data: T }
export interface ApiErr {
  ok: false
  error: { code: string; message: string; details?: unknown }
}
export type ApiResult<T> = ApiOk<T> | ApiErr

function svc(service: string, path: string): string {
  return `${API_BASE}/v1/${service}${path}`
}

async function send<T>(url: string, init?: RequestInit): Promise<ApiResult<T>> {
  try {
    const res = await fetch(url, {
      ...init,
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
        ...(init?.headers ?? {}),
      },
    })
    const text = await res.text()
    const body = text
      ? (JSON.parse(text) as ApiResult<T>)
      : ({ ok: false, error: { code: 'EMPTY', message: 'empty body' } } as ApiErr)
    if (!res.ok && !('ok' in body)) {
      return { ok: false, error: { code: `HTTP_${res.status}`, message: res.statusText } }
    }
    return body
  } catch (e) {
    return { ok: false, error: { code: 'NETWORK', message: e instanceof Error ? e.message : 'network error' } }
  }
}

// ── Tenants by slug ─────────────────────────────────────────────────────────
export interface TenantBySlug { tenantId: string; slug: string; name: string; active: boolean }
export async function getTenantBySlug(slug: string) {
  return send<TenantBySlug>(svc('dhk8-platform-tenants', `/v1/tenants/by-slug/${encodeURIComponent(slug)}`))
}

// ── Nombre de sucursal (público) — para mostrar a QUÉ sucursal ordena el comensal ──
export interface BranchName { name: string }
export async function getBranchName(branchId: string) {
  return send<BranchName>(svc('dhk8-platform-tenants', `/v1/public/branch/${encodeURIComponent(branchId)}`))
}

// ── Loyalty status (público, sin PII): gatea el hook de "acumula puntos" ──────
export interface LoyaltyStatusDto { enabled: boolean; pointsPerVisit: number }
export async function getLoyaltyStatus(tenantId: string) {
  return send<LoyaltyStatusDto>(svc('dhk8-guest-sessions', `/v1/guest-sessions/loyalty-status?tenantId=${encodeURIComponent(tenantId)}`))
}

// ── Floor QR resolve ────────────────────────────────────────────────────────
export interface QrResolveDto {
  tenantId: string; branchId: string | null; mapId: string
  tableId: string; tableCode: string
  tableStatus: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'CLEANING' | 'UNAVAILABLE'
  /** `TABLE` = QR de mesa; `TAKEOUT` = QR para llevar (pasa por pago primero). */
  kind: 'TABLE' | 'TAKEOUT'
}
export async function resolveQr(qrCode: string) {
  return send<QrResolveDto>(svc('dhk8-pos-floor', `/v1/floor/qr/${encodeURIComponent(qrCode)}/resolve`))
}

// ── Guest check-in / join / me / refresh ────────────────────────────────────
export interface FromQrBody { slug?: string; qrCode: string; name?: string; phone?: string }
export interface CheckInBody { qrCode: string; name: string; phone?: string; email?: string }
export interface JoinBody { slug?: string; qrCode: string; joinCode: string; name?: string; phone?: string }
export interface CheckInResponse {
  token: string; sessionId: string; tableSessionId: string
  tenantId: string; branchId: string | null
  tableId: string; tableCode: string
  expiresAt: string; menuUrl: string; customerName: string
  /** Código de unión de la sesión de mesa OhSir (compartible). */
  joinCode: string | null
  /** true si el comensal se unió a una sesión que ya existía. */
  joinedExisting: boolean
  /**
   * OhSir: modo de la orden de esta sesión.
   *  - `DINE_IN`  → QR de mesa: al enviar, la orden va directo a cocina.
   *  - `TAKEOUT`  → QR para llevar: la orden pasa por PAGO antes de cocina.
   * `orderMode` puede venir ausente en backends viejos → tratar como DINE_IN.
   */
  orderMode?: 'DINE_IN' | 'TAKEOUT'
}
/** Detalle que el backend adjunta a un error JOIN_CODE_REQUIRED. */
export interface JoinCodeRequiredDetails { tableCode: string; tenantId: string }

/**
 * Abre la sesión privada de la mesa (primer comensal). Si la mesa YA tiene una
 * sesión activa, el backend responde error `JOIN_CODE_REQUIRED` (HTTP 409) y el
 * PWA debe pedir el código de unión.
 */
export async function checkInFromQr(body: FromQrBody) {
  return send<CheckInResponse>(svc('dhk8-guest-sessions', `/v1/guest-sessions/from-qr`), {
    method: 'POST',
    body: JSON.stringify(body),
  })
}
export async function checkIn(body: CheckInBody) {
  return send<CheckInResponse>(svc('dhk8-guest-sessions', `/v1/guest-sessions/check-in`), {
    method: 'POST',
    body: JSON.stringify(body),
  })
}
/**
 * Se une a la sesión privada YA ABIERTA de la mesa con el código compartido.
 * Código incorrecto → error `JOIN_CODE_INVALID`.
 */
export async function joinSession(body: JoinBody) {
  return send<CheckInResponse>(svc('dhk8-guest-sessions', `/v1/guest-sessions/join`), {
    method: 'POST',
    body: JSON.stringify(body),
  })
}
export async function fetchMe(token: string) {
  return send<CheckInResponse>(svc('dhk8-guest-sessions', `/v1/guest-sessions/me`), {
    headers: { authorization: `Bearer ${token}` },
  })
}

// ── Identify (paso opcional 'acumula puntos') ───────────────────────────────
export interface IdentifyBody { name: string; phone?: string; email?: string }
export interface IdentifyResponse {
  sessionId: string
  customerName: string
  customerPhone: string | null
  loyalty: {
    contactId: string
    loyaltyPoints: number
    loyaltyVisits: number
    pointsEarned: number
  } | null
}
/** Registra al comensal en el CRM ligero y acumula puntos de lealtad. */
export async function identifyGuest(token: string, body: IdentifyBody) {
  return send<IdentifyResponse>(svc('dhk8-guest-sessions', `/v1/guest-sessions/identify`), {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
}

// ── Public active menu ──────────────────────────────────────────────────────
export interface MenuItemDto {
  id: string; name: string; priceCents: number; currency: string
  imageUrl: string | null; unit: string | null; sku: string; description: string | null
}
export interface MenuSectionDto { id: string; name: string; sortOrder: number; items: MenuItemDto[] }
export interface ActiveMenuDto { id: string; name: string; sections: MenuSectionDto[] }
export async function fetchActiveMenu(tenantId: string, token: string, branchId?: string | null) {
  const qs = new URLSearchParams({ tenantId })
  if (branchId) qs.set('branchId', branchId)
  // Gateado por sesión de mesa: el endpoint exige el token guest (aud=guest) y
  // saca el tenantId del token. tenantId en query queda solo informativo.
  return send<{ menu: ActiveMenuDto }>(
    svc('dhk8-catalog-menus', `/v1/menus/active/public?${qs.toString()}`),
    { headers: { authorization: `Bearer ${token}` } },
  )
}

// ── Modifier groups (per producto) ──────────────────────────────────────────
/** Mirror de services/dhk8-catalog-modifiers/src/contract.ts. El POS consume el
 *  mismo GET /v1/modifier-groups?tenantId=&productId= (filtra por producto vía
 *  el link product↔group). Envelope: { ok, data }. */
export interface ModifierOptionDto {
  id: string
  groupId: string
  label: string
  priceDeltaCents: number
  nestedGroupId: string | null
  active: boolean
  sortOrder: number
}
export interface ModifierGroupDto {
  id: string
  tenantId: string
  name: string
  minSelect: number
  maxSelect: number
  isRequired: boolean
  active: boolean
  deletedAt: string | null
  options: ModifierOptionDto[]
}
export interface ModifierGroupListResponse {
  items: ModifierGroupDto[]
  total: number
}
/** Trae los grupos de modificadores vinculados a un producto. El comensal es
 *  ANÓNIMO, así que usa la variante PÚBLICA `/v1/modifier-groups/public`
 *  (tenantGuardSkip) — el endpoint autenticado `/v1/modifier-groups` exige
 *  permiso staff y devolvía 401. Espejo del menú público (`menus/active/public`). */
export async function fetchProductModifiers(tenantId: string, productId: string, token: string) {
  const qs = new URLSearchParams({ tenantId, productId, limit: '100' })
  // Gateado por sesión de mesa (token guest). El tenantId real lo toma el backend
  // del token; el del query queda informativo.
  return send<ModifierGroupListResponse>(
    svc('dhk8-catalog-modifiers', `/v1/modifier-groups/public?${qs.toString()}`),
    { headers: { authorization: `Bearer ${token}` } },
  )
}

// ── Orders (guest scope) ────────────────────────────────────────────────────
/** Body para POST /v1/orders. branchId/openedByUserId los auto-inyecta el
 *  guest-auth hook desde los claims del JWT — el cliente puede omitirlos. */
/** Métodos de pago (enum canónico del backend; el gate valida contra esto). */
export type PaymentMethod = 'CASH' | 'TERMINAL' | 'ONLINE'

export interface CreateOrderInput {
  tenantId: string
  tableSessionId?: string
  /** `DINE_IN` (mesa, va directo a cocina) o `TAKEOUT` (para llevar, pasa por
   *  pago — la orden NO se dispara a cocina hasta que el staff confirma). */
  type?: 'DINE_IN' | 'TAKEOUT'
  /** Método de pago que elige el comensal; lo valida el gate de aceptación del
   *  staff. Debe ser uno de los métodos habilitados por el negocio. */
  chosenPaymentMethod?: PaymentMethod
  /** Nonce de idempotencia del checkout: un 2º POST con la misma llave (doble-tap
   *  o retry de red) devuelve la MISMA orden en vez de crear otra. */
  idempotencyKey?: string
}

/** Gate de métodos habilitados por el negocio. `null` = no configurado. */
export interface PaymentMethodsGate {
  cashEnabled: boolean
  terminalEnabled: boolean
  onlineEnabled: boolean
  /** Presets de propina (%) que el negocio eligió para el guest (ej. [0,10,15]).
   *  `[]`/ausente = el negocio no configuró propina → el guest usa su default en
   *  el prepago online y NO ofrece propina en pago en mostrador (back-comp:
   *  backends viejos no mandan el campo). */
  tipPresets?: number[]
}
/**
 * Lee los métodos de pago habilitados del negocio. El tenant se deriva server-side
 * del token guest (claims.tid) — no se envía. `data.methods === null` significa que
 * el negocio aún no configura métodos (el selector lo refleja).
 */
export async function fetchPaymentMethods(token: string) {
  return send<{ methods: PaymentMethodsGate | null }>(
    svc('dhk8-pos-orders', `/v1/payment-methods`),
    { headers: { authorization: `Bearer ${token}` } },
  )
}
export interface OrderItemModifierDto {
  id: string; orderItemId: string; modifierId: string; name: string; priceDeltaCents: number
}
export interface OrderItemDto {
  id: string; productId: string; productName?: string; name?: string
  quantity: number; unitPriceCents: number; status: string; notes: string | null
  /** Ronda (tanda) en la que se disparó — agrupa los pedidos hechos en momentos
   *  distintos. Null mientras sigue en el carrito (antes de someter). */
  roundId?: string | null
  modifiers?: OrderItemModifierDto[]
}
export interface OrderDto {
  id: string; tenantId: string; branchId: string | null
  type: string; status: string; tableSessionId: string | null
  /** Método de pago elegido por el comensal — lo usa Payment.tsx para despachar
   *  TAKEOUT+ONLINE a la captura Stripe. El backend lo devuelve en la orden. */
  chosenPaymentMethod?: PaymentMethod | null
  subtotalCents: number; taxCents: number; tipCents: number; totalCents: number
  paidCents?: number
  /** Folio real de la orden (ORD-000123), secuencial por sucursal. Es el que el
   *  staff puede buscar en el POS — a diferencia del `orderId.slice(0,8)` que la
   *  app inventaba antes y que no correspondía a nada del lado del negocio. */
  folio?: string | null
  ticketFolio?: number | null
  /** Token del ticket público imprimible → https://tickets.nintaigo.com/{uuid}. */
  publicTicketUuid?: string | null
  /** Código de recolección (`XXXX-99999`) que el comensal presenta al recoger su
   *  pedido para llevar. Lo genera el SERVIDOR al crear la orden y el staff lo
   *  valida antes de entregar. Null en pedidos de mesa (se entregan en la mesa). */
  pickupCode?: string | null
  deliveredAt?: string | null
  items: OrderItemDto[]
  createdAt?: string; openedAt?: string
}
export async function createOrder(token: string, body: CreateOrderInput) {
  return send<OrderDto>(svc('dhk8-pos-orders', `/v1/orders`), {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
}

/** Respuesta del intent de pago en línea (gateway). El monto es autoritativo
 *  (lo fija el servidor desde la orden); `publishableKey` (pk público) inicializa
 *  Stripe.js; `clientSecret` confirma el PaymentIntent en el cliente. */
export interface PaymentIntentDto {
  paymentId: string
  intentId: string
  clientSecret?: string
  publishableKey: string | null
  amountCents: number       // total cobrado = consumo + propina
  subtotalCents: number     // consumo (saldo de la orden)
  tipCents: number          // propina validada por el servidor
  processor: string
}

/** Crea el PaymentIntent de UNA orden (cobro digital del comensal). El comensal
 *  manda el orderId + propina elegida; el consumo lo pone el servidor (autoritativo)
 *  y la propina se valida (tope 100%). */
export async function createPaymentIntent(token: string, orderId: string, tipCents = 0) {
  return send<PaymentIntentDto>(svc('dhk8-payments-gateway', `/v1/payments/intent`), {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: JSON.stringify({ orderId, tipCents }),
  })
}

/** Estado + evidencia de un pago (para la pantalla de recibo). `status` es la
 *  VERDAD del backend: CAPTURED sólo cuando el webhook de Stripe lo confirmó. */
export interface PaymentStatusDto {
  id: string
  status: string                 // PENDING | CAPTURED | FAILED | REFUNDED
  amountCents: number
  processorChargeId: string | null
  evidence: {
    cardBrand?: string
    cardLast4?: string
    threeDSecure?: string
    riskLevel?: string
    receiptUrl?: string
  } | null
}

/** Consulta un pago por id (sondeo del recibo). */
export async function getPayment(token: string, paymentId: string) {
  return send<PaymentStatusDto>(svc('dhk8-payments-gateway', `/v1/payments/${paymentId}`), {
    headers: { authorization: `Bearer ${token}` },
  })
}

export interface AddItemBody {
  productId: string
  name: string
  quantity: number
  /** Precio BASE del producto. El backend suma los deltas de `modifiers`:
   *  lineGross = (unitPriceCents + Σ modifier.priceDeltaCents) * quantity. */
  unitPriceCents: number
  modifiers?: { modifierId: string; name: string; priceDeltaCents: number }[]
  notes?: string
}
export async function addOrderItem(token: string, orderId: string, body: AddItemBody) {
  return send<OrderDto>(svc('dhk8-pos-orders', `/v1/orders/${orderId}/items`), {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
}

/** ADR-0013 F1: dispara el descuento de lealtad server-authoritative sobre la
 *  orden (el monto lo calcula el servidor desde el nivel del comensal). Se llama
 *  tras armar la orden y ANTES de pagar; el gateway lee el total ya descontado.
 *  Best-effort en el caller: un fallo no debe bloquear el pago. */
export async function applyLoyalty(token: string, orderId: string) {
  return send<OrderDto>(svc('dhk8-pos-orders', `/v1/orders/${orderId}/apply-loyalty`), {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
  })
}

/** Registra la propina elegida por el comensal en la orden (pago en MOSTRADOR:
 *  efectivo/terminal — la propina viaja en la orden y se cobra en caja). El
 *  prepago online NO usa esta ruta: ahí la propina va en el intent del gateway. */
export async function setOrderTip(token: string, orderId: string, tipCents: number) {
  return send<OrderDto>(svc('dhk8-pos-orders', `/v1/orders/${orderId}/tip`), {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: JSON.stringify({ tipCents }),
  })
}

/** PAGO DE DEMOSTRACIÓN (modo estadio) — simulación, SIN pasarela. El servidor
 *  sólo lo acepta para negocios habilitados (DEMO_PAYMENT_TENANT_IDS) y deja el
 *  pedido pagado como un prepago real (cierra + cocina). Ver lib/stadium.ts. */
export async function payOrderDemo(token: string, orderId: string) {
  return send<OrderDto>(svc('dhk8-pos-orders', `/v1/orders/${orderId}/demo-payment`), {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: '{}',
  })
}

export async function fireOrder(token: string, orderId: string) {
  return send<OrderDto>(svc('dhk8-pos-orders', `/v1/orders/${orderId}/fire`), {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: '{}',
  })
}

/**
 * Somete una RONDA: cuando el comensal agrega a una cuenta YA abierta (segundo
 * pedido en adelante), agrupa lo nuevo en una ronda que el mesero debe aceptar.
 * La ronda 1 no pasa por aquí — nace con la orden en PENDING_ACCEPTANCE y el
 * staff acepta la ORDEN. Sin esto, lo que el comensal pide después se quedaba
 * suelto y nunca llegaba a cocina.
 */
export async function submitRound(token: string, orderId: string) {
  return send<OrderDto>(svc('dhk8-pos-orders', `/v1/orders/${orderId}/submit-round`), {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: JSON.stringify({ origin: 'guest' }),
  })
}
export async function getOrder(token: string, orderId: string) {
  return send<OrderDto>(svc('dhk8-pos-orders', `/v1/orders/${orderId}`), {
    headers: { authorization: `Bearer ${token}` },
  })
}

/**
 * OhSir pago de MESA — la cuenta abierta de la mesa del comensal.
 *
 * El comensal del QR de mesa (DINE_IN) no tiene un orderId a mano: ordenó, la
 * orden se fue a cocina y él sólo guarda su sesión. Este getter resuelve la
 * cuenta viva de su mesa desde el `tableSessionId` del token guest (el backend
 * lo lee de los claims — el comensal no puede leer la cuenta de otra mesa).
 *
 * Devuelve error `NOT_FOUND` si la mesa aún no tiene cuenta (nadie ha enviado
 * a cocina) o si ya está cerrada/pagada. Sesión compartida: varios comensales
 * ven y pagan LA MISMA cuenta de mesa.
 */
export async function getMyTableOrder(token: string) {
  return send<OrderDto>(svc('dhk8-pos-orders', `/v1/orders/by-session`), {
    headers: { authorization: `Bearer ${token}` },
  })
}

// ── Floor guest signals (mesero / cuenta) ───────────────────────────────────
export async function callWaiter(token: string, tableSessionId: string) {
  return send<{ tableSessionId: string; accepted: boolean; signal: string }>(
    svc('dhk8-pos-floor', `/v1/floor/sessions/${tableSessionId}/call-waiter`),
    {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
      body: '{}',
    },
  )
}
export async function requestBill(token: string, tableSessionId: string) {
  return send<{ tableSessionId: string; accepted: boolean; signal: string }>(
    svc('dhk8-pos-floor', `/v1/floor/sessions/${tableSessionId}/request-bill`),
    {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
      body: '{}',
    },
  )
}

// ── OhSir notificaciones categorizadas (guest → POS) ─────────────────────────
/** Temas de notificación OhSir — espejo del catálogo del backend
 *  (services/dhk8-pos-floor/src/services/guest-notifications.service.ts). */
export type GuestNotificationTopic =
  | 'CALL_WAITER'
  | 'REQUEST_CHECK'
  | 'REQUEST_ITEM'
  | 'REPORT_ISSUE'
  | 'OTHER'

export interface GuestNotificationResult {
  notification: {
    id: string
    topic: GuestNotificationTopic
    note: string | null
    status: string
    createdAt: string
  }
  /** true si ya había una notificación pendiente del mismo tema. */
  deduped: boolean
}

/** El comensal manda una notificación categorizada con un TEMA. La nota es
 *  opcional (detalle del "Otro" o del problema reportado). */
export async function sendGuestNotification(
  token: string,
  tableSessionId: string,
  topic: GuestNotificationTopic,
  note?: string,
) {
  const body: { topic: GuestNotificationTopic; note?: string } = { topic }
  if (note && note.trim()) body.note = note.trim()
  return send<GuestNotificationResult>(
    svc('dhk8-pos-floor', `/v1/floor/sessions/${tableSessionId}/notify`),
    {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    },
  )
}
